import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";
import { useWebSocket } from "./WebSocketContext";

/**
 * Lightweight shared state for pending Gym Buddy invites.
 *
 * Why a separate context (vs. fetching inside Social.js)?
 *   - We need to surface a badge on the bottom-nav Squad icon AND a banner on the
 *     Dashboard, both of which live OUTSIDE Social.js. Pulling /buddies in both
 *     places would double-fetch and drift.
 *   - Polls every 60s and on `visibilitychange` so the badge stays fresh without
 *     requiring a manual refresh — important because buddy requests can arrive
 *     while the app is open in a background tab.
 */
const BuddyInvitesContext = createContext({
  incoming: [],
  count: 0,
  refresh: () => {},
  accept: async () => {},
  decline: async () => {},
});

const POLL_MS = 60_000;

export function BuddyInvitesProvider({ children }) {
  const { user } = useAuth();
  const { subscribe } = useWebSocket();
  const [incoming, setIncoming] = useState([]);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setIncoming([]);
      return;
    }
    try {
      const { data } = await api.get("/buddies");
      setIncoming(Array.isArray(data?.incoming) ? data.incoming : []);
    } catch {
      // silent — keep prior state on transient errors
    }
  }, [user]);

  useEffect(() => {
    refresh();
    if (timerRef.current) clearInterval(timerRef.current);
    if (user) {
      // Polling is the safety net. The WS subscription below is what makes
      // invites feel instant when the app is open.
      timerRef.current = setInterval(refresh, POLL_MS);
    }
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user, refresh]);

  // Live updates via WebSocket — the banner + badge react within ~200ms of
  // the other user tapping "Invite", no manual refresh needed.
  useEffect(() => {
    if (!user) return undefined;
    const unsubReq = subscribe("buddy:request", (b) => {
      // Prepend if not already there
      setIncoming((prev) => prev.some((x) => x.request_id === b.request_id) ? prev : [b, ...prev]);
    });
    const unsubAcc = subscribe("buddy:accepted", () => {
      // Our outgoing request was accepted — full refresh so the Buddies tab
      // shows the new accepted pair on next render.
      refresh();
    });
    return () => { unsubReq(); unsubAcc(); };
  }, [user, subscribe, refresh]);

  const accept = useCallback(async (request_id) => {
    // Optimistic remove so the badge/banner clear instantly
    setIncoming((prev) => prev.filter((b) => b.request_id !== request_id));
    try {
      await api.post("/buddies/accept", { request_id });
    } catch {
      refresh(); // resync on failure
      throw new Error("accept_failed");
    }
  }, [refresh]);

  const decline = useCallback(async (request_id) => {
    setIncoming((prev) => prev.filter((b) => b.request_id !== request_id));
    try {
      await api.delete(`/buddies/${request_id}`);
    } catch {
      refresh();
      throw new Error("decline_failed");
    }
  }, [refresh]);

  return (
    <BuddyInvitesContext.Provider value={{ incoming, count: incoming.length, refresh, accept, decline }}>
      {children}
    </BuddyInvitesContext.Provider>
  );
}

export const useBuddyInvites = () => useContext(BuddyInvitesContext);
